import React, { useEffect, useMemo, useState } from 'react';
import {
  deriveGuidanceSummary,
  flattenGuidanceTree,
  guidanceLayoutConstants,
  personalOverlayOpacity,
} from './derivation';
import { filterGuidanceTreeEligibleOnly, filterGuidanceTreeHideCompleted, filterGuidanceTreeWaypointsOnly, initialGuidanceCollapsedState, applyEligibleQueueScheduling, ensureEligibleActivitiesInTree } from './guidanceTreeUi';
import {
  buildCriticalPathSegments,
  buildDependencyPaths,
  buildGuidanceRowGeometries,
  committedPx,
  GUIDANCE_INDENT_PX,
  GUIDANCE_STATUS_COLORS,
  guidanceTodayWeek,
  guidanceNameMap,
  formatGuidanceCounts,
  guidanceSublabel,
  guidanceGlanceTiles,
  guidanceDagFromLayout,
  guidanceFloatRows,
  projectedPx,
  showsCommittedMark,
  weekAxisLabels,
} from './guidanceGanttHelpers';
import {
  adjustZoom,
  guidanceViewportFromManifest,
  todayLinePx,
  viewportFromDateRange,
  weekIndexToIsoDate,
  weekWidthForViewport,
  type GuidanceViewport,
} from './guidanceViewport';
import {
  computePathHighlight,
  defaultPathFocusActivityId,
  filterDependencyPaths,
  filterGuidanceRowsByPathHighlight,
  pathFilterOpacity,
  togglePathModeList,
  type GuidancePathDisplayMode,
  type GuidancePathMode,
} from './guidancePathFilter';
import {
  guidanceBodyHeightPx,
  guidanceRowHeightPx,
  showsGuidanceDependencyOverlay,
  showsGuidanceGanttBar,
  showsGuidanceGanttChart,
} from './guidanceViewMode';
import { GuidanceNetworkView } from '../guidance/GuidanceNetworkView';
import type { GuidanceHiFiModel, GuidanceTreeNode, GuidanceViewMode, PersonalOverlayMode } from './types';
import type { KryspaceControlBridgeStructure } from '../../../types/kryspaceControlBridgeStructure';
import type { ControlBridgeQueueItem } from '../deriveViewModel';
import { queueItemOrFallback, findQueueItemById } from '../detail/findQueueItemById';
import { resolveGuidanceNodeArtifactInstanceId } from '../detail/resolveArtifactInstanceId';
import {
  ControlBridgeDecomposeError,
  canValidateImprovementItem,
  decomposeImprovement,
  resolveDecomposeAssignedResourceRef,
} from '../../../services/controlBridgeDecomposeService';
import {
  ControlBridgeExoPanelExecutionError,
  executeExoPanelAction,
} from '../../../services/controlBridgeExoPanelExecutionService';
import { isCreatedStatusPhrase, isDraftStatusPhrase } from '../../../utils/statusPhrasePlane';
import styles from '../ControlBridge.module.css';

export interface PersonalLayerGuidanceProxyProps {
  model: GuidanceHiFiModel;
  overlayMode: PersonalOverlayMode;
  structure?: KryspaceControlBridgeStructure;
  queueItems?: ControlBridgeQueueItem[];
  workspaceSlug?: string;
  onManifestRefresh?: () => void | Promise<void>;
  onOpenArtifactDetail?: (artifactInstanceId: string, invokingLabel: string) => void;
  onOpenItemDetail?: (item: ControlBridgeQueueItem, invokingLabel?: string) => void;
  /** Row / label click → Chat shelf route (U4). Details ↗ keeps onOpenItemDetail overlay. */
  onSelectItem?: (item: ControlBridgeQueueItem, invokingLabel?: string) => void;
  onOpenSurface?: (surface: 'diagnose' | 'drive') => void;
}

function CountsPill({ counts }: { counts: NonNullable<GuidanceTreeNode['counts']> }) {
  return (
    <span className={styles.guidanceCountsPill} data-testid="guidance-counts-pill">
      {formatGuidanceCounts(counts)}
    </span>
  );
}

export const PersonalLayerGuidanceProxy: React.FC<PersonalLayerGuidanceProxyProps> = ({
  model,
  overlayMode,
  structure,
  queueItems = [],
  workspaceSlug,
  onManifestRefresh,
  onOpenArtifactDetail,
  onOpenItemDetail,
  onSelectItem,
  onOpenSurface,
}) => {
  const defaultCollapsed = useMemo(
    () => initialGuidanceCollapsedState(model.root),
    [model.root]
  );
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => defaultCollapsed);
  const [decomposeBusyId, setDecomposeBusyId] = useState<string | null>(null);
  const [decomposeNotice, setDecomposeNotice] = useState<string | null>(null);
  /** Hide Create on improvements that just applied a child (queue may lag INITIALIZED). */
  const [recentlyCreatedImprovementIds, setRecentlyCreatedImprovementIds] = useState<
    Record<string, true>
  >({});
  const [recentlyStartedActivityIds, setRecentlyStartedActivityIds] = useState<
    Record<string, true>
  >({});
  const [recentlyValidatedImprovementIds, setRecentlyValidatedImprovementIds] = useState<
    Record<string, true>
  >({});
  const [startBusyId, setStartBusyId] = useState<string | null>(null);
  const [validateBusyId, setValidateBusyId] = useState<string | null>(null);
  const [showProjected, setShowProjected] = useState(true);
  const [showCommitted, setShowCommitted] = useState(true);
  const [hideCompleted, setHideCompleted] = useState(() => model.filterHints?.hideCompletedDefault ?? false);
  const [eligibleOnly, setEligibleOnly] = useState(() => model.filterHints?.eligibleOnlyDefault ?? false);
  const [pathModes, setPathModes] = useState<GuidancePathMode[]>([]);
  const [pathFocusId, setPathFocusId] = useState<string | null>(null);
  const [pathDisplayMode, setPathDisplayMode] = useState<GuidancePathDisplayMode>('dim');
  const [viewMode, setViewMode] = useState<GuidanceViewMode>('traditional');
  const [viewport, setViewport] = useState<GuidanceViewport>(() =>
    guidanceViewportFromManifest(model.weekCount, model.weekWidthPx, model.timelineWindow)
  );

  const todayWeek = model.todayWeek ?? model.timelineWindow?.todayWeek ?? guidanceTodayWeek();

  useEffect(() => {
    setViewport(guidanceViewportFromManifest(model.weekCount, model.weekWidthPx, model.timelineWindow));
  }, [model.weekCount, model.weekWidthPx, model.sessionId, model.timelineWindow]);

  useEffect(() => {
    setHideCompleted(model.filterHints?.hideCompletedDefault ?? false);
    setEligibleOnly(model.filterHints?.eligibleOnlyDefault ?? false);
  }, [model.filterHints, model.sessionId]);

  const rangeStartIso = weekIndexToIsoDate(viewport.startWeek);
  const rangeEndIso = weekIndexToIsoDate(viewport.startWeek + viewport.weekCount - 1);

  useEffect(() => {
    setCollapsed(defaultCollapsed);
  }, [defaultCollapsed]);

  const scheduledRoot = useMemo(() => {
    let root = applyEligibleQueueScheduling(model.root, model.eligibleWork ?? []);
    root = ensureEligibleActivitiesInTree(root, model.eligibleWork ?? []);
    if (hideCompleted) root = filterGuidanceTreeHideCompleted(root);
    if (eligibleOnly) root = filterGuidanceTreeEligibleOnly(root, model.eligibleWork ?? []);
    return root;
  }, [eligibleOnly, hideCompleted, model.root, model.eligibleWork]);
  const displayRoot = useMemo(
    () => filterGuidanceTreeWaypointsOnly(scheduledRoot),
    [scheduledRoot]
  );
  const displaySummary = useMemo(() => deriveGuidanceSummary(displayRoot), [displayRoot]);
  const glanceTiles = useMemo(() => guidanceGlanceTiles(scheduledRoot), [scheduledRoot]);
  const waypointDag = useMemo(
    () => guidanceDagFromLayout(scheduledRoot, model.layoutHint),
    [scheduledRoot, model.layoutHint]
  );
  const floatOrder = useMemo(() => guidanceFloatRows(scheduledRoot), [scheduledRoot]);
  const [openFloatId, setOpenFloatId] = useState<string | null>(null);

  const pathHighlight = useMemo(
    () => computePathHighlight(displayRoot, pathModes, pathFocusId),
    [displayRoot, pathModes, pathFocusId]
  );

  const effectivePathFocusId = useMemo(() => {
    if (pathFocusId && pathHighlight?.has(pathFocusId)) return pathFocusId;
    if (pathModes.some((m) => m === 'forward' || m === 'backward')) {
      return defaultPathFocusActivityId(displayRoot);
    }
    return pathFocusId;
  }, [pathFocusId, pathHighlight, pathModes, displayRoot]);

  useEffect(() => {
    if (pathModes.length === 0 && pathDisplayMode === 'hide') {
      setPathDisplayMode('dim');
    }
  }, [pathModes, pathDisplayMode]);

  const layout = guidanceLayoutConstants();
  const flatRows = useMemo(
    () => flattenGuidanceTree(displayRoot, collapsed),
    [displayRoot, collapsed]
  );
  const rows = useMemo(
    () => filterGuidanceRowsByPathHighlight(flatRows, pathHighlight, pathDisplayMode),
    [flatRows, pathHighlight, pathDisplayMode]
  );
  const nameById = useMemo(() => guidanceNameMap(displayRoot), [displayRoot]);
  const weekWidthPx = weekWidthForViewport(viewport);
  const geometries = useMemo(
    () => buildGuidanceRowGeometries(rows, weekWidthPx, viewport.startWeek, viewport.weekCount),
    [rows, viewport.startWeek, viewport.weekCount, weekWidthPx]
  );
  const geomById = useMemo(
    () => Object.fromEntries(geometries.map((g) => [g.nodeId, g])),
    [geometries]
  );

  const chartWidth = viewport.chartWidthPx;
  const bodyHeight = guidanceBodyHeightPx(rows, viewMode, layout.rowHeightPx);
  const showDepOverlay = showsGuidanceDependencyOverlay(viewMode);
  const meMode = overlayMode === 'me';
  const weekLabels = weekAxisLabels(viewport.weekCount, {
    startWeek: viewport.startWeek,
    todayWeek,
  });
  const todayPx = todayLinePx(todayWeek, viewport);

  const depPaths = useMemo(
    () => buildDependencyPaths(rows, geomById, model.treeColumnWidthPx),
    [rows, geomById, model.treeColumnWidthPx]
  );
  const critPaths = useMemo(
    () => buildCriticalPathSegments(rows, geomById, model.treeColumnWidthPx),
    [rows, geomById, model.treeColumnWidthPx]
  );
  const visibleDepPaths = useMemo(
    () => (showDepOverlay ? filterDependencyPaths(depPaths, rows, pathHighlight) : []),
    [depPaths, pathHighlight, rows, showDepOverlay]
  );
  const showCritOverlay =
    showDepOverlay && (pathModes.length === 0 || pathModes.includes('critical') || !pathHighlight);
  const visibleCritPaths = showCritOverlay ? critPaths : [];

  const handlePathModeToggle = (mode: GuidancePathMode) => {
    setPathModes((prev) => togglePathModeList(prev, mode));
  };

  const toggleCollapse = (id: string) => {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const runGuidanceDecompose = async (node: GuidanceTreeNode, apply: boolean) => {
    const slug = workspaceSlug?.trim();
    if (!slug || node.type !== 'improvement') return;
    setDecomposeBusyId(node.id);
    setDecomposeNotice(null);
    try {
      const queueItem = findQueueItemById(queueItems, node.id, {
        matchImprovementRef: true,
      });
      const result = await decomposeImprovement(slug, {
        improvementRef: node.id,
        apply,
        name: node.plankDescription ?? node.label,
        assignedResourceRef: resolveDecomposeAssignedResourceRef({
          assignedResourceRef: queueItem?.assignedResourceRef,
          resourceAssignment: node.resourceAssignment ?? queueItem?.resourceAssignment,
          queueItems,
        }),
      });
      if (!apply) {
        const patchId =
          result.artifactPatch && typeof result.artifactPatch.item_id === 'string'
            ? result.artifactPatch.item_id
            : null;
        setDecomposeNotice(
          patchId
            ? `Preview ready for activity ${patchId}. Use Create activity to persist.`
            : `Preview ready for “${node.label.slice(0, 48)}${node.label.length > 48 ? '…' : ''}”.`
        );
        return;
      }
      setRecentlyCreatedImprovementIds((prev) => ({ ...prev, [node.id]: true }));
      await onManifestRefresh?.();
      setDecomposeNotice(`Activity created for “${node.label.slice(0, 48)}${node.label.length > 48 ? '…' : ''}”.`);
    } catch (err: unknown) {
      setDecomposeNotice(
        err instanceof ControlBridgeDecomposeError ? err.message : 'Create activity failed'
      );
    } finally {
      setDecomposeBusyId(null);
    }
  };

  const runGuidanceStart = async (node: GuidanceTreeNode) => {
    const slug = workspaceSlug?.trim();
    if (!slug || node.type !== 'activity') return;
    const phrase =
      node.statusPhrase ??
      findQueueItemById(queueItems, node.id)?.statusPhrase ??
      findQueueItemById(model.eligibleWork ?? [], node.id)?.statusPhrase;
    if (!isCreatedStatusPhrase(phrase) && !isDraftStatusPhrase(phrase)) return;
    setStartBusyId(node.id);
    setDecomposeNotice(null);
    try {
      const result = await executeExoPanelAction(slug, {
        itemId: node.id,
        action: 'start',
      });
      await onManifestRefresh?.();
      setRecentlyStartedActivityIds((prev) => ({ ...prev, [node.id]: true }));
      setDecomposeNotice(
        `Started “${node.label.slice(0, 48)}${node.label.length > 48 ? '…' : ''}”` +
          (result.toState ? ` → ${result.toState}` : ' → ACTIVE')
      );
    } catch (err: unknown) {
      setDecomposeNotice(
        err instanceof ControlBridgeExoPanelExecutionError ? err.message : 'Start failed'
      );
      if (
        err instanceof ControlBridgeExoPanelExecutionError &&
        err.code === 'PERSIST_MODE_NOT_PATCH'
      ) {
        try {
          await onManifestRefresh?.();
        } catch {
          // Keep persistMode error primary.
        }
      }
    } finally {
      setStartBusyId(null);
    }
  };

  const runGuidanceValidate = async (node: GuidanceTreeNode) => {
    const slug = workspaceSlug?.trim();
    if (!slug || node.type !== 'improvement') return;
    const phrase =
      node.statusPhrase ??
      findQueueItemById(queueItems, node.id)?.statusPhrase ??
      undefined;
    if (!canValidateImprovementItem('pt-improvement-activity', phrase)) return;
    setValidateBusyId(node.id);
    setDecomposeNotice(null);
    try {
      const result = await executeExoPanelAction(slug, {
        itemId: node.id,
        action: 'validate',
      });
      await onManifestRefresh?.();
      setRecentlyValidatedImprovementIds((prev) => ({ ...prev, [node.id]: true }));
      setDecomposeNotice(
        `Validated “${node.label.slice(0, 48)}${node.label.length > 48 ? '…' : ''}”` +
          (result.toState ? ` → ${result.toState}` : ' → VALIDATED')
      );
    } catch (err: unknown) {
      setDecomposeNotice(
        err instanceof ControlBridgeExoPanelExecutionError ? err.message : 'Validate failed'
      );
      if (
        err instanceof ControlBridgeExoPanelExecutionError &&
        err.code === 'PERSIST_MODE_NOT_PATCH'
      ) {
        try {
          await onManifestRefresh?.();
        } catch {
          // Keep persistMode error primary.
        }
      }
    } finally {
      setValidateBusyId(null);
    }
  };

  return (
    <div className={styles.personalLayerPanel} data-testid="personal-layer-guidance-proxy">
      {decomposeNotice ? (
        <p className={styles.entityDetailStatus} role="status">
          {decomposeNotice}
        </p>
      ) : null}
      <div className={styles.personalLayerSummaryHiFi} aria-label="Guidance summary">
        <div className={`${styles.personalLayerSummaryCard} ${styles.personalLayerSummaryAccent}`}>
          <span className={styles.personalLayerSummaryLabel}>{displaySummary.whenLabel}</span>
          <span className={styles.personalLayerSummaryValue}>{displaySummary.whenRange}</span>
        </div>
        <div className={styles.personalLayerSummaryCard}>
          <span className={styles.personalLayerSummaryLabel}>Why this long</span>
          <span className={styles.personalLayerSummaryValue}>
            {displaySummary.criticalPathWeeks.toFixed(1)} weeks
          </span>
        </div>
        <div className={styles.personalLayerSummaryCard}>
          <span className={styles.personalLayerSummaryLabel}>What moves the date</span>
          <span className={styles.personalLayerSummaryValue}>
            {displaySummary.leverageCount} leverage · {displaySummary.blockerCount} blockers
          </span>
        </div>
        <div className={styles.personalLayerSummaryCard}>
          <span className={styles.personalLayerSummaryLabel}>Scope & sufficiency</span>
          <span className={styles.personalLayerSummaryValue}>{displaySummary.scopeLabel}</span>
        </div>
        {glanceTiles.map((tile) => (
          <div key={tile.key} className={styles.personalLayerSummaryCard} data-testid={`guidance-glance-${tile.key}`}>
            <span className={styles.personalLayerSummaryLabel}>{tile.question}</span>
            <span className={styles.personalLayerSummaryValue}>{tile.value}</span>
          </div>
        ))}
      </div>
      {waypointDag ? (
        <div className={styles.guidanceDag} data-testid="guidance-waypoint-dag">
          {Array.from(new Set(waypointDag.nodes.map((node) => node.column))).map((column) => (
            <div key={column} className={styles.guidanceDagColumn}>
              {waypointDag.nodes
                .filter((node) => node.column === column)
                .map((node) => (
                  <div
                    key={node.id}
                    className={`${styles.guidanceDagNode} ${
                      node.critical ? styles.guidanceDagNodeCritical : ''
                    } ${node.floatUnknown ? styles.guidanceDagNodeUnknown : ''}`}
                    data-critical={node.critical ? 'true' : 'false'}
                    data-float-unknown={node.floatUnknown ? 'true' : 'false'}
                  >
                    <span>{node.label}</span>
                    {node.isEnd && node.expectedRange ? (
                      <span className={styles.guidanceDagRange}>Expected {node.expectedRange}</span>
                    ) : null}
                  </div>
                ))}
            </div>
          ))}
          {waypointDag.edges.length > 0 ? (
            <ul className={styles.guidanceDagEdges}>
              {waypointDag.edges.map((edge) => {
                const from = waypointDag.nodes.find((node) => node.id === edge.from);
                const to = waypointDag.nodes.find((node) => node.id === edge.to);
                if (!from || !to) return null;
                return (
                  <li
                    key={`${edge.from}-${edge.to}`}
                    data-critical={edge.critical ? 'true' : 'false'}
                    className={edge.critical ? styles.guidanceDagEdgeCritical : undefined}
                  >
                    {from.label} → {to.label}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      ) : null}
      {onOpenSurface ? (
        <div className={styles.guidanceLegendBar}>
          <button type="button" className={styles.guidanceMarkToggle} onClick={() => onOpenSurface('diagnose')}>
            Diagnostics
          </button>
          <button type="button" className={styles.guidanceMarkToggle} onClick={() => onOpenSurface('drive')}>
            Drive constraint step
          </button>
        </div>
      ) : null}
      {floatOrder.length > 0 ? (
        <ul data-testid="guidance-float-order">
          {floatOrder.map((node) => {
            const open = openFloatId === node.id;
            return (
              <li key={node.id}>
                <button type="button" onClick={() => setOpenFloatId(open ? null : node.id)}>
                  {node.label}
                  {node.floatWeeks == null ? ' · float unknown' : ` · float ${node.floatWeeks}`}
                </button>
                {open ? (
                  <div>
                    <div>What is it waiting on? {node.waitingOn}</div>
                    <div>
                      {node.secondQuestion} {node.secondAnswer}
                    </div>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      <div className={styles.guidanceLegendBar}>
        <span className={styles.guidanceLegendLabel}>View</span>
        <div className={styles.guidanceViewModeGroup} role="group" aria-label="Gantt density">
          <button
            type="button"
            className={`${styles.guidanceMarkToggle} ${viewMode === 'traditional' ? styles.guidanceMarkToggleOn : ''}`}
            aria-pressed={viewMode === 'traditional'}
            onClick={() => setViewMode('traditional')}
          >
            Traditional
          </button>
          <button
            type="button"
            className={`${styles.guidanceMarkToggle} ${viewMode === 'compact' ? styles.guidanceMarkToggleOn : ''}`}
            aria-pressed={viewMode === 'compact'}
            onClick={() => setViewMode('compact')}
          >
            Compact
          </button>
          <button
            type="button"
            className={`${styles.guidanceMarkToggle} ${viewMode === 'network' ? styles.guidanceMarkToggleOn : ''}`}
            aria-pressed={viewMode === 'network'}
            onClick={() => setViewMode('network')}
          >
            Network
          </button>
        </div>
        <span className={styles.guidanceLegendDivider} aria-hidden />
        <button
          type="button"
          className={`${styles.guidanceMarkToggle} ${hideCompleted ? styles.guidanceMarkToggleOn : ''}`}
          aria-pressed={hideCompleted}
          onClick={() => setHideCompleted((v) => !v)}
        >
          Hide completed
        </button>
        <span className={styles.guidanceLegendDivider} aria-hidden />
        <span className={styles.guidanceLegendLabel}>Path</span>
        <div className={styles.guidancePathModeGroup} role="group" aria-label="Dependency path filter">
          <button
            type="button"
            className={`${styles.guidanceMarkToggle} ${pathModes.includes('forward') ? styles.guidanceMarkToggleOn : ''}`}
            aria-pressed={pathModes.includes('forward')}
            onClick={() => handlePathModeToggle('forward')}
          >
            Forward
          </button>
          <button
            type="button"
            className={`${styles.guidanceMarkToggle} ${pathModes.includes('backward') ? styles.guidanceMarkToggleOn : ''}`}
            aria-pressed={pathModes.includes('backward')}
            onClick={() => handlePathModeToggle('backward')}
          >
            Backward
          </button>
          <button
            type="button"
            className={`${styles.guidanceMarkToggle} ${pathModes.includes('critical') ? styles.guidanceMarkToggleOn : ''}`}
            aria-pressed={pathModes.includes('critical')}
            onClick={() => handlePathModeToggle('critical')}
          >
            Critical
          </button>
          <button
            type="button"
            className={`${styles.guidanceMarkToggle} ${pathDisplayMode === 'hide' ? styles.guidanceMarkToggleOn : ''}`}
            aria-pressed={pathDisplayMode === 'hide'}
            disabled={pathModes.length === 0}
            title={pathModes.length === 0 ? 'Enable a path mode first' : undefined}
            onClick={() =>
              setPathDisplayMode((mode) => (mode === 'hide' ? 'dim' : 'hide'))
            }
          >
            Hide others
          </button>
        </div>
        <span className={styles.guidanceLegendDivider} aria-hidden />
        <span className={styles.guidanceLegendLabel}>Window</span>
        <div className={styles.guidanceTimescaleControls} role="group" aria-label="Timeline window">
          <button
            type="button"
            className={styles.guidanceTimescaleBtn}
            aria-label="Zoom out — show more weeks in view"
            onClick={() => setViewport((v) => adjustZoom(v, -1))}
          >
            −
          </button>
          <button
            type="button"
            className={styles.guidanceTimescaleBtn}
            aria-label="Zoom in — enlarge activity bars"
            onClick={() => setViewport((v) => adjustZoom(v, 1))}
          >
            +
          </button>
        </div>
        <label className={styles.guidanceDateRangeLabel}>
          <span className={styles.guidanceLegendLabel}>From</span>
          <input
            type="date"
            className={styles.guidanceDateInput}
            value={rangeStartIso}
            aria-label="Timeline range start"
            onChange={(e) => {
              const next = viewportFromDateRange(e.target.value, rangeEndIso, viewport.chartWidthPx);
              if (next) setViewport(next);
            }}
          />
        </label>
        <label className={styles.guidanceDateRangeLabel}>
          <span className={styles.guidanceLegendLabel}>To</span>
          <input
            type="date"
            className={styles.guidanceDateInput}
            value={rangeEndIso}
            aria-label="Timeline range end"
            onChange={(e) => {
              const next = viewportFromDateRange(rangeStartIso, e.target.value, viewport.chartWidthPx);
              if (next) setViewport(next);
            }}
          />
        </label>
        <span className={styles.guidanceLegendDivider} aria-hidden />
        <span className={styles.guidanceLegendLabel}>Marks</span>
        <button
          type="button"
          className={`${styles.guidanceMarkToggle} ${showProjected ? styles.guidanceMarkToggleOn : ''}`}
          aria-pressed={showProjected}
          onClick={() => setShowProjected((v) => !v)}
        >
          <span className={styles.guidanceMarkDiamond} aria-hidden />
          Projected
        </button>
        <button
          type="button"
          className={`${styles.guidanceMarkToggle} ${showCommitted ? styles.guidanceMarkToggleOn : ''}`}
          aria-pressed={showCommitted}
          onClick={() => setShowCommitted((v) => !v)}
        >
          <span className={styles.guidanceMarkFlag} aria-hidden />
          Committed
        </button>
      </div>

      {showsGuidanceGanttChart(viewMode) ? (
      <div
        className={`${styles.guidanceGanttCard} ${viewMode === 'compact' ? styles.guidanceGanttCompact : ''}`}
      >
        <div className={styles.guidanceGanttScroll}>
          <div
            className={styles.guidanceGanttInner}
            style={{ width: '100%', minWidth: model.treeColumnWidthPx + chartWidth }}
          >
            <div className={styles.guidanceGanttAxis}>
              <div
                className={styles.guidanceGanttTreeHeadHiFi}
                style={{ width: model.treeColumnWidthPx }}
              >
                Work
              </div>
              <div className={styles.guidanceGanttWeekAxis} style={{ minWidth: chartWidth, flex: 1 }}>
                {weekLabels.map((label, i) => (
                  <span
                    key={`${viewport.startWeek}-${label}`}
                    className={`${styles.guidanceGanttWeekTickHiFi} ${viewport.startWeek + i === Math.floor(todayWeek) ? styles.guidanceGanttWeekToday : ''}`}
                    style={{ width: weekWidthPx }}
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>

            <div className={styles.guidanceGanttBody} style={{ height: bodyHeight }} role="tree" aria-label="Guidance timeline">
              {showDepOverlay ? (
                <svg
                  className={styles.guidanceGanttGridSvg}
                  width={chartWidth}
                  height={bodyHeight}
                  style={{ left: model.treeColumnWidthPx }}
                  aria-hidden
                >
                  {Array.from({ length: viewport.weekCount + 1 }, (_, i) => (
                    <line
                      key={i}
                      x1={i * weekWidthPx}
                      y1={0}
                      x2={i * weekWidthPx}
                      y2={bodyHeight}
                      stroke="#f1f3f5"
                      strokeWidth={1}
                    />
                  ))}
                </svg>
              ) : null}

              {showDepOverlay ? (
              <svg
                className={styles.guidanceGanttConnectorSvg}
                width={chartWidth}
                height={bodyHeight}
                style={{ left: model.treeColumnWidthPx }}
                aria-hidden
              >
                <defs>
                  <marker id="depArrow" markerWidth="7" markerHeight="7" refX="5" refY="3" orient="auto">
                    <path d="M0 0 L5.5 3 L0 6 Z" fill="#c2cad6" />
                  </marker>
                  <marker id="critArrow" markerWidth="7" markerHeight="7" refX="5" refY="3" orient="auto">
                    <path d="M0 0 L5.5 3 L0 6 Z" fill="#ef4444" />
                  </marker>
                </defs>
                {visibleDepPaths.map((p) => (
                  <path
                    key={p.id}
                    d={p.d}
                    fill="none"
                    stroke={p.stroke}
                    strokeWidth={p.strokeWidth}
                    markerEnd={`url(#${p.markerEnd})`}
                  />
                ))}
                {visibleCritPaths.map((p) => (
                  <path
                    key={p.id}
                    d={p.d}
                    fill="none"
                    stroke={p.stroke}
                    strokeWidth={p.strokeWidth}
                    markerEnd={`url(#${p.markerEnd})`}
                  />
                ))}
                {todayPx != null ? (
                  <line
                    x1={todayPx}
                    y1={0}
                    x2={todayPx}
                    y2={bodyHeight}
                    stroke="#0066ff"
                    strokeWidth={1.5}
                    strokeDasharray="4 3"
                  />
                ) : null}
              </svg>
              ) : null}

              {!showDepOverlay && todayPx != null ? (
                <svg
                  className={styles.guidanceGanttConnectorSvg}
                  width={chartWidth}
                  height={bodyHeight}
                  style={{ left: model.treeColumnWidthPx }}
                  aria-hidden
                >
                  <line
                    x1={todayPx}
                    y1={0}
                    x2={todayPx}
                    y2={bodyHeight}
                    stroke="#0066ff"
                    strokeWidth={1.5}
                    strokeDasharray="4 3"
                  />
                </svg>
              ) : null}

              {rows.map((row, index) => {
                const { node, depth, isCollapsible, isCollapsed } = row;
                const showBar = showsGuidanceGanttBar(node.type, viewMode);
                const rowHeight = guidanceRowHeightPx(node.type, viewMode, layout.rowHeightPx);
                const geom = geometries[index]!;
                const sc = GUIDANCE_STATUS_COLORS[node.status];
                const sub = guidanceSublabel(
                  node,
                  nameById,
                  model.sessionLabel,
                  model.objectiveLabel
                );
                const pct =
                  node.status === 'done' ? 1 : node.status === 'active' ? (node.pct ?? 0.5) : 0;
                const rowBg =
                  !showBar && viewMode === 'compact'
                    ? '#fafbfc'
                    : node.type === 'session'
                      ? '#eef4ff'
                      : node.type === 'waypoint'
                        ? '#fafbfc'
                        : '#fff';

                const pathFocused = node.type === 'activity' && node.id === effectivePathFocusId;
                const rowOpacity =
                  personalOverlayOpacity(node.mine, overlayMode) *
                  pathFilterOpacity(node.id, pathHighlight, pathDisplayMode);
                const artifactInstanceId =
                  node.type === 'waypoint' || node.type === 'improvement'
                    ? resolveGuidanceNodeArtifactInstanceId(node, structure)
                    : undefined;
                const canOpenItemDetail =
                  (node.type === 'activity' ||
                    node.type === 'waypoint' ||
                    node.type === 'improvement') &&
                  Boolean(onOpenItemDetail);
                const canOpenArtifactDetail =
                  (node.type === 'waypoint' || node.type === 'improvement') &&
                  Boolean(onOpenArtifactDetail && artifactInstanceId);
                const showCreateActivity =
                  node.type === 'improvement' &&
                  Boolean(workspaceSlug?.trim()) &&
                  !(node.children ?? []).some((child) => child.type === 'activity') &&
                  !recentlyCreatedImprovementIds[node.id];
                const activityStatusPhrase =
                  node.statusPhrase ??
                  findQueueItemById(queueItems, node.id)?.statusPhrase ??
                  findQueueItemById(model.eligibleWork ?? [], node.id)?.statusPhrase;
                const showStartActivity =
                  node.type === 'activity' &&
                  Boolean(workspaceSlug?.trim()) &&
                  !recentlyStartedActivityIds[node.id] &&
                  (isCreatedStatusPhrase(activityStatusPhrase) ||
                    isDraftStatusPhrase(activityStatusPhrase));
                const improvementStatusPhrase =
                  node.statusPhrase ??
                  findQueueItemById(queueItems, node.id)?.statusPhrase;
                const showValidateImprovement =
                  node.type === 'improvement' &&
                  Boolean(workspaceSlug?.trim()) &&
                  !recentlyValidatedImprovementIds[node.id] &&
                  canValidateImprovementItem('pt-improvement-activity', improvementStatusPhrase);

                const openGuidanceDetail = () => {
                  if (canOpenItemDetail && onOpenItemDetail) {
                    onOpenItemDetail(
                      queueItemOrFallback(queueItems, node.id, {
                        title: node.label,
                        statusPhrase: node.status,
                        improvementRef: node.type === 'improvement' ? node.id : undefined,
                        plankDescription: node.plankDescription,
                        futureCondition: node.futureCondition,
                        resourceAssignment: node.resourceAssignment,
                        assumptionText: node.assumptionText,
                      }),
                      'Guidance'
                    );
                    return;
                  }
                  if (canOpenArtifactDetail && onOpenArtifactDetail && artifactInstanceId) {
                    onOpenArtifactDetail(artifactInstanceId, 'Guidance');
                  }
                };

                const openGuidanceChat = () => {
                  if (!onSelectItem) return;
                  onSelectItem(
                    queueItemOrFallback(queueItems, node.id, {
                      title: node.label,
                      statusPhrase: node.status,
                      improvementRef: node.type === 'improvement' ? node.id : undefined,
                      plankDescription: node.plankDescription,
                      futureCondition: node.futureCondition,
                      resourceAssignment: node.resourceAssignment,
                      assumptionText: node.assumptionText,
                      blockerClass: node.critical ? 'critical' : node.blocker ? 'warning' : undefined,
                    }),
                    'Guidance'
                  );
                };

                return (
                  <div
                    key={node.id}
                    className={[
                      styles.guidanceGanttRowHiFi,
                      !showBar ? styles.guidanceGanttRowListOnly : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    role="treeitem"
                    aria-expanded={isCollapsible ? !isCollapsed : undefined}
                    style={{
                      height: rowHeight,
                      opacity: rowOpacity,
                    }}
                  >
                    <div
                      className={[
                        styles.guidanceGanttTreeCellHiFi,
                        meMode && node.mine ? styles.guidanceGanttTreeMine : '',
                        pathFocused ? styles.guidanceGanttTreePathFocus : '',
                        isCollapsible ? styles.guidanceGanttTreeCellCollapsible : '',
                        !showBar ? styles.guidanceGanttTreeCellListOnly : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={{
                        width: showBar ? model.treeColumnWidthPx : undefined,
                        paddingLeft: 14 + depth * GUIDANCE_INDENT_PX,
                        background: rowBg,
                      }}
                    >
                      {isCollapsible ? (
                        <button
                          type="button"
                          className={styles.guidanceGanttChevronBtn}
                          aria-expanded={!isCollapsed}
                          aria-label={isCollapsed ? 'Expand row' : 'Collapse row'}
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleCollapse(node.id);
                          }}
                        >
                          <span
                            className={`${styles.guidanceGanttChevron} ${isCollapsed ? '' : styles.guidanceGanttChevronOpen}`}
                            aria-hidden
                          />
                        </button>
                      ) : (
                        <span className={styles.guidanceGanttCollapseSpacer} aria-hidden />
                      )}
                      <span className={styles.guidanceStatusDot} style={{ background: sc.dot }} />
                      <div className={styles.guidanceGanttTreeText}>
                        {onSelectItem ? (
                          <button
                            type="button"
                            className={styles.guidanceGanttLabelHiFi}
                            onClick={(event) => {
                              event.stopPropagation();
                              openGuidanceChat();
                            }}
                          >
                            {node.label}
                          </button>
                        ) : (
                          <div className={styles.guidanceGanttLabelHiFi}>{node.label}</div>
                        )}
                        {sub ? (
                          <div
                            className={`${styles.guidanceGanttSublabel} ${sub.mono ? styles.guidanceGanttSublabelMono : ''}`}
                          >
                            {sub.text}
                          </div>
                        ) : null}
                      </div>
                      <div className={styles.guidanceGanttMetaIcons}>
                        {node.critical ? (
                          <span className={styles.guidanceMetaCritical} title="Critical path">
                            ▲
                          </span>
                        ) : null}
                        {node.blocker ? (
                          <span className={styles.guidanceMetaBlocker} title={node.blockerTitle ?? 'Blocked'}>
                            ⚠{node.blockerCount ? node.blockerCount : ''}
                          </span>
                        ) : null}
                        {node.leverage ? (
                          <span className={styles.guidanceMetaLeverage} title="Leverage point">
                            ⤴
                          </span>
                        ) : null}
                        {isCollapsible && node.counts ? <CountsPill counts={node.counts} /> : null}
                        {canOpenItemDetail || canOpenArtifactDetail ? (
                          <button
                            type="button"
                            className={styles.guidanceDetailLink}
                            onClick={(event) => {
                              event.stopPropagation();
                              openGuidanceDetail();
                            }}
                          >
                            Details ↗
                          </button>
                        ) : null}
                        {showValidateImprovement ? (
                          <button
                            type="button"
                            className={styles.guidanceDetailLink}
                            data-testid="guidance-validate-improvement"
                            disabled={validateBusyId === node.id}
                            onClick={(event) => {
                              event.stopPropagation();
                              void runGuidanceValidate(node);
                            }}
                          >
                            {validateBusyId === node.id ? 'Validating…' : 'Validate'}
                          </button>
                        ) : null}
                        {showStartActivity ? (
                          <button
                            type="button"
                            className={styles.guidanceDetailLink}
                            data-testid="guidance-start-activity"
                            disabled={startBusyId === node.id}
                            onClick={(event) => {
                              event.stopPropagation();
                              void runGuidanceStart(node);
                            }}
                          >
                            {startBusyId === node.id ? 'Starting…' : 'Start'}
                          </button>
                        ) : null}
                        {showCreateActivity ? (
                          <>
                            <button
                              type="button"
                              className={styles.guidanceDetailLink}
                              data-testid="guidance-preview-activity"
                              disabled={decomposeBusyId === node.id}
                              onClick={(event) => {
                                event.stopPropagation();
                                void runGuidanceDecompose(node, false);
                              }}
                            >
                              {decomposeBusyId === node.id ? 'Working…' : 'Preview activity'}
                            </button>
                            <button
                              type="button"
                              className={styles.guidanceDetailLink}
                              data-testid="guidance-create-activity"
                              disabled={decomposeBusyId === node.id}
                              onClick={(event) => {
                                event.stopPropagation();
                                void runGuidanceDecompose(node, true);
                              }}
                            >
                              {decomposeBusyId === node.id ? 'Creating…' : 'Create activity'}
                            </button>
                          </>
                        ) : null}
                        {meMode && node.mine ? <span className={styles.personalYouTag}>YOU</span> : null}
                      </div>
                    </div>

                    {showBar ? (
                    <div
                      className={styles.guidanceGanttTrackHiFi}
                      style={{ minWidth: chartWidth, flex: 1 }}
                    >
                      <div
                        className={styles.guidanceGanttBarWrap}
                        style={{
                          left: geom.left,
                          width: geom.earlyWidth + geom.tailWidth,
                          top: (rowHeight - layout.barHeightPx) / 2,
                          height: layout.barHeightPx,
                        }}
                      >
                        <div
                          className={styles.guidanceGanttBarEarlyHiFi}
                          style={{
                            width: geom.earlyWidth,
                            background: sc.track,
                            borderColor: sc.border,
                          }}
                        >
                          {pct > 0 ? (
                            <div
                              className={styles.guidanceGanttBarFill}
                              style={{ width: `${pct * 100}%`, background: sc.fill }}
                            />
                          ) : null}
                        </div>
                        {geom.tailWidth > 0 ? (
                          <div
                            className={styles.guidanceGanttBarLateHiFi}
                            style={{
                              left: geom.earlyWidth,
                              width: geom.tailWidth,
                              borderColor: sc.border,
                              backgroundImage: `repeating-linear-gradient(135deg, ${sc.stripe} 0 4px, transparent 4px 8px)`,
                            }}
                          />
                        ) : null}
                        <div
                          className={styles.guidanceGanttBarEndCap}
                          style={{ left: geom.earlyWidth + geom.tailWidth - 1, background: sc.border }}
                        />
                        {showProjected && node.range.l > node.range.e ? (
                          <div
                            className={styles.guidanceGanttProjectedMark}
                            style={{
                              left:
                                projectedPx(node.range, weekWidthPx, viewport.startWeek) -
                                geom.left -
                                4,
                            }}
                            title="P50 projected"
                          />
                        ) : null}
                        {showCommitted && showsCommittedMark(node) ? (
                          <div
                            className={styles.guidanceGanttCommittedMark}
                            style={{
                              left:
                                committedPx(node.range, weekWidthPx, viewport.startWeek) -
                                geom.left,
                            }}
                            title="P85 committed"
                          />
                        ) : null}
                      </div>
                    </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      ) : (
        <div className={styles.guidanceGanttCard}>
          <GuidanceNetworkView
            root={displayRoot}
            focusActivityId={effectivePathFocusId}
            onSelectActivity={(id) => setPathFocusId(id)}
          />
        </div>
      )}
    </div>
  );
};
